/* eslint-disable react/no-unescaped-entities */
import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { Fragment } from 'react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';
import SvgIcon from '@components/common/SvgIcon';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import IcPlay from '@public/icons/ic_play.svg';
import ProfileImg from '@public/icons/profile_32.svg';

import { IContentByProfileByChoiceFromGallery } from '@base/outlookModel';
import { useGlobalContext } from '@contexts/global';
import { formatNumberMillion } from '@utils/chat';

const containerCSS = css`
  .header {
    display: flex;
    justify-content: space-between;
    padding: 10px ${Spacing.medium}px 0;
  }
  .gridContainer {
    display: grid;
    grid-auto-flow: column;
    column-gap: ${Spacing.xsmall}px;
    row-gap: 10px;
    padding: 6px 16px ${Spacing.large}px;
    margin-top: ${Spacing.medium}px;
  }

  .with-quotes {
    position: relative;
  }
  .with-quotes::after,
  .with-quotes::before {
    content: '"';
  }
  .with-quotes::after {
    position: absolute;
    right: 0px;
    top: 0px;
    content: '"';
    z-index: 9;
  }
`;
const infoContainerCSS = (theme: Theme) => css`
  display: flex;
  flex: 1;
  width: 100%;

  .imageContainer {
    width: 36px;
    height: 36px;
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
    position: relative;
    overflow: hidden;
  }
  .image {
    width: 36px;
    height: 36px;
    background-color: ${theme.var.gray200};
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
  }
  .infoContainer {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
  .name {
    ${Typography.body12};
  }
  .info {
    ${Typography.body11};
    color: ${theme.var.gray500};
    margin-top: ${Spacing.xhsmall}px;
    display: flex;
  }
`;

const cardContainerCSS = css`
  width: 304px;
  display: flex;
  flex-direction: column;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.small}px;
  box-shadow: 0px 0px 10px 2px rgba(0, 0, 0, 0.05);
`;

const startMessageContainer = css`
  text-align: start;
  margin-top: ${Spacing.small}px;
  min-height: 36px;
  max-height: 36px;

  span {
    line-height: 145%;
    display: inline-block;
  }
`;

const choicesContainer = (theme: Theme) => css`
  padding-bottom: 14px;

  .choice-background-container {
    margin-top: ${Spacing.small}px;
    width: 280px;
    height: 60px;
  }

  .choice-container {
    display: flex;
    width: 100%;
    height: 100%;
    padding: 14px ${Spacing.small}px;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    border-radius: 5px;
    border: 1px solid ${theme.var.gray200};
    background: ${theme.var.gray100};
    gap: ${Spacing.xsmall}px;
  }
`;

const choiceMessageCss =
  (isParagraph: boolean, maxWidth = 256) =>
  (theme: Theme) =>
    css`
      max-width: ${maxWidth - 5}px;
      padding-right: 5px;
      color: ${isParagraph ? theme.var.gray500 : theme.var.gray900};
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      word-wrap: break-word;
      word-break: break-all;

      ${Typography.body12};
    `;

interface ProfileByChoiceFromGalleryProps {
  data: IContentByProfileByChoiceFromGallery;
  componentKey: string;
  onClickGroundCharacrer?: (id: number) => void;
  onClickChoice?: (idx: number, situationId: number) => void;
  order?: number;
}

const ProfileByChoiceFromGallery = ({
  data,
  onClickGroundCharacrer,
  order,
  onClickChoice,
}: ProfileByChoiceFromGalleryProps) => {
  const { t } = useTranslation();
  const { isCheming } = useGlobalContext();
  const { title, profiles, choice_count } = data;
  const presetKey = order + 'ProfileByChoiceFromGallery';
  return (
    <div css={containerCSS}>
      <div className="header">
        <h5>{t(title)}</h5>
      </div>
      <SwipeableListContainer className="galleryCardListContainer">
        <div className="gridContainer">
          {profiles?.map((item, idx) => {
            const startMessages =
              item.payload.dynamic_object.situation.start_messages;

            return (
              <div css={cardContainerCSS} key={`info_list_${idx}`}>
                <div
                  css={infoContainerCSS}
                  onClick={() =>
                    onClickGroundCharacrer
                      ? onClickGroundCharacrer(item.id)
                      : undefined
                  }
                >
                  <div className="imageContainer">
                    {item.image && item.image.key ? (
                      <EFinderImage
                        image={item.image}
                        h={EFinderImageSizes.avatar}
                        alt={'profile_img'}
                      />
                    ) : (
                      <div className="image">
                        <SvgIcon icon={ProfileImg} d={36} />
                      </div>
                    )}
                  </div>
                  <div className="infoContainer">
                    <h6 className="name">{item.title}</h6>
                    {!isCheming && (
                      <p className="info">
                        <SvgIcon icon={IcPlay} />
                        {formatNumberMillion(
                          item.payload.dynamic_object.message_count ?? 0
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div
                  className="discription"
                  css={startMessageContainer}
                  key={presetKey + 'startMessageContainer' + 'idx:'}
                >
                  {Array.from({ length: 2 }, (_, startMessageIndex) => {
                    const innerIdx =
                      startMessageIndex === 0
                        ? startMessages?.length - 2
                        : startMessages?.length - 1;
                    const hasContent = !!startMessages[innerIdx];
                    const message = hasContent
                      ? startMessages[innerIdx]
                      : { content: '', type: 'character' };
                    const isCharacrer = message?.type !== 'narration';
                    const content = message?.content;

                    return (
                      <Fragment
                        key={`order:${order}-idx:${idx}-startMessageIndex:${startMessageIndex}-message`}
                      >
                        {hasContent && (
                          <Fragment>
                            <span
                              css={choiceMessageCss(!isCharacrer, 280)}
                              className={isCharacrer ? 'with-quotes' : ''}
                            >
                              {t(
                                isCharacrer
                                  ? content
                                  : content.replaceAll('*', '')
                              )}
                            </span>
                            <br />
                          </Fragment>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
                <div css={choicesContainer}>
                  {Array.from({ length: choice_count }, (_, index) => {
                    const hasChoice = !!item.choices[index];
                    const contents = item.choices
                      ? item.choices[index]?.content?.split('\n')
                      : [];
                    const length = contents?.length >= 2 ? 2 : contents?.length;

                    return (
                      <div
                        key={presetKey + 'choicesContainer' + 'idx:' + index}
                        className="choice-background-container"
                        onClick={() =>
                          onClickChoice
                            ? onClickChoice(
                                index,
                                item.payload.dynamic_object.situation.id
                              )
                            : undefined
                        }
                      >
                        {hasChoice && (
                          <div className="choice-container">
                            {Array.from(
                              { length: length },
                              (_, contentIndex) => {
                                const hasContent = !!contents[contentIndex];

                                const content = hasContent
                                  ? contents[contentIndex]
                                  : '';
                                const isParagraph = content?.includes('*');

                                return (
                                  <p
                                    key={
                                      'order:' +
                                      order +
                                      'startMessageContainer' +
                                      'idx:' +
                                      contentIndex +
                                      'innerIdx : ' +
                                      index
                                    }
                                    css={choiceMessageCss(isParagraph)}
                                    className={
                                      !isParagraph ? 'with-quotes' : ''
                                    }
                                  >
                                    {t(
                                      isParagraph
                                        ? content?.replaceAll('*', '')
                                        : content?.replaceAll('"', '')
                                    )}
                                  </p>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </SwipeableListContainer>
    </div>
  );
};

export default ProfileByChoiceFromGallery;
